import { Link } from 'react-router-dom';
import styles from './Research.module.css';

const PAPERS = [
  {
    title: 'Numerical Methods for Deterministic Physics Simulation',
    authors: 'AxLabs Technical Team',
    status: 'published',
    description: 'Describes the integration schemes, stability criteria, and error bounds used across all physics simulations. Covers symplectic integrators for conservation laws.',
    year: 2024,
  },
  {
    title: 'Validation Methodology for Educational Science Simulations',
    authors: 'AxLabs Technical Team',
    status: 'published',
    description: 'Framework for validating virtual laboratory experiments against analytical solutions, experimental data, and established computational results.',
    year: 2024,
  },
  {
    title: 'Reproducibility in Web-Based Science Education',
    authors: 'AxLabs Technical Team',
    status: 'draft',
    description: 'Analysis of reproducibility challenges in browser-based simulations and solutions for deterministic computation across platforms.',
    year: 2025,
  },
];

const VALIDATION = [
  {
    category: 'Analytical Solutions',
    description: 'Simulations are tested against known analytical solutions where available. Pendulum motion compared to elliptic integral solutions. Circuit analysis compared to closed-form transient response.',
  },
  {
    category: 'Energy Conservation',
    description: 'Mechanical simulations monitored for energy drift. Symplectic integrators used where conservation is critical. Drift reported to users when it exceeds meaningful thresholds.',
  },
  {
    category: 'Convergence Testing',
    description: 'Time-step refinement studies confirm convergence. Results compared across multiple step sizes to ensure numerical stability. Extrapolation to continuum limit validated.',
  },
  {
    category: 'Experimental Comparison',
    description: 'Selected experiments compared to physical laboratory data. Discrepancies documented and explained. Model limitations clearly communicated.',
  },
];

const ACCURACY = [
  {
    metric: 'Time Integration',
    standard: 'Symplectic Euler / RK4',
    accuracy: 'Fourth-order local error, energy conservation to 10⁻⁶ per period for conservative systems',
  },
  {
    metric: 'Physical Constants',
    standard: 'CODATA 2022',
    accuracy: 'Values and uncertainties from NIST/CODATA recommended values',
  },
  {
    metric: 'Numerical Precision',
    standard: 'IEEE 754 double precision',
    accuracy: 'Machine epsilon ~2.2×10⁻¹⁶, careful avoidance of catastrophic cancellation',
  },
  {
    metric: 'Reproducibility',
    standard: 'Deterministic execution',
    accuracy: 'Bit-identical results across runs, platforms, and JavaScript engines given same parameters',
  },
];

const OPEN_PROBLEMS = [
  {
    problem: 'Stiff Systems',
    description: 'Chemical kinetics with widely varying rate constants present numerical challenges. Adaptive step-size methods needed for efficient simulation.',
  },
  {
    problem: 'Chaotic Dynamics',
    description: 'Sensitive dependence on initial conditions limits long-term prediction. Ensemble methods and uncertainty quantification under development.',
  },
  {
    problem: 'Multi-Scale Physics',
    description: 'Systems spanning multiple time/length scales require specialized algorithms. Molecular dynamics to continuum transitions remain challenging.',
  },
  {
    problem: 'Browser Performance',
    description: 'Real-time visualization with numerical accuracy requires careful optimization. WebAssembly integration for compute-intensive simulations.',
  },
];

const CONTRIBUTION_AREAS = [
  {
    area: 'New Experiments',
    description: 'Propose and implement new simulation experiments following AxLabs methodology. Subject to validation and review.',
  },
  {
    area: 'Validation Studies',
    description: 'Compare simulation outputs to experimental data or analytical solutions. Document discrepancies and suggest improvements.',
  },
  {
    area: 'Numerical Methods',
    description: 'Improve integration schemes, implement adaptive algorithms, or develop new methods for specific problem classes.',
  },
  {
    area: 'Documentation',
    description: 'Improve theoretical background, add examples, or clarify methodology. Educational value is a priority.',
  },
];

export function Research() {
  return (
    <div className={styles.research}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <Link to="/" className={styles.backLink}>← Back to Home</Link>
          <h1 className={styles.title}>Research</h1>
          <p className={styles.subtitle}>
            Technical documentation, validation methodology, and ongoing research.
            AxLabs is built on published principles and open to external contribution.
          </p>
        </div>
      </header>

      {/* Overview */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Technical Foundation</h2>
          <div className={styles.prose}>
            <p className={styles.proseText}>
              AxLabs simulations are built on established numerical methods and physical principles.
              Unlike educational games that approximate behavior for visual effect, our simulations
              solve actual differential equations with documented error characteristics.
            </p>
            <p className={styles.proseText}>
              This technical foundation enables legitimate scientific inquiry. Students can explore
              parameter space, compare results to theory, and understand the limits of models.
              Instructors can design assessments with objectively correct answers.
            </p>
          </div>
        </div>
      </section>

      {/* Technical Papers */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Technical Papers</h2>
          <div className={styles.papersList}>
            {PAPERS.map((paper) => (
              <article key={paper.title} className={styles.paperCard}>
                <div className={styles.paperHeader}>
                  <div>
                    <span className={`${styles.paperStatus} ${styles[paper.status]}`}>
                      {paper.status === 'published' && 'Published'}
                      {paper.status === 'draft' && 'Draft'}
                      {paper.status === 'submitted' && 'Submitted'}
                    </span>
                    <span className={styles.paperYear}>{paper.year}</span>
                  </div>
                </div>
                <h3 className={styles.paperTitle}>{paper.title}</h3>
                <p className={styles.paperAuthors}>{paper.authors}</p>
                <p className={styles.paperDesc}>{paper.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Validation Methodology */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Validation Methodology</h2>
          <p className={styles.sectionNote}>
            Every experiment undergoes validation before release. Ongoing validation tracks
            numerical accuracy across platform updates.
          </p>
          <div className={styles.validationGrid}>
            {VALIDATION.map((item) => (
              <div key={item.category} className={styles.validationCard}>
                <h3 className={styles.validationCategory}>{item.category}</h3>
                <p className={styles.validationDesc}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numerical Accuracy */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Numerical Accuracy</h2>
          <p className={styles.sectionNote}>
            Standards and achievable accuracy for simulation computations.
            All metrics represent typical performance; specific experiments may vary.
          </p>
          <div className={styles.accuracyTable}>
            <div className={styles.tableRow}>
              <span className={styles.tableHeader}>Metric</span>
              <span className={styles.tableHeader}>Standard</span>
              <span className={styles.tableHeader}>Achievable Accuracy</span>
            </div>
            {ACCURACY.map((item) => (
              <div key={item.metric} className={styles.tableRow}>
                <span className={styles.tableCell}>{item.metric}</span>
                <span className={styles.tableCellMono}>{item.standard}</span>
                <span className={styles.tableCell}>{item.accuracy}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Problems */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Open Problems</h2>
          <p className={styles.sectionNote}>
            Active areas of research and development. These problems represent
            opportunities for contribution and collaboration.
          </p>
          <div className={styles.problemsGrid}>
            {OPEN_PROBLEMS.map((problem) => (
              <div key={problem.problem} className={styles.problemCard}>
                <h3 className={styles.problemTitle}>{problem.problem}</h3>
                <p className={styles.problemDesc}>{problem.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contribution Guidelines */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Contribution Guidelines</h2>
          <p className={styles.sectionNote}>
            AxLabs welcomes contributions from researchers, educators, and developers.
            All contributions are subject to technical review and validation.
          </p>
          <div className={styles.contributionList}>
            {CONTRIBUTION_AREAS.map((area) => (
              <div key={area.area} className={styles.contributionCard}>
                <h3 className={styles.contributionTitle}>{area.area}</h3>
                <p className={styles.contributionDesc}>{area.description}</p>
              </div>
            ))}
          </div>
          <div className={styles.contributionFooter}>
            <p className={styles.contributionNote}>
              To contribute, open an issue on GitHub or submit a pull request.
              Include validation evidence for any numerical or scientific claims.
            </p>
            <a
              href="https://github.com/staticpayload/axlabs"
              className={styles.contributeLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contribute on GitHub →
            </a>
          </div>
        </div>
      </section>

      {/* Citation */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Citation</h2>
          <div className={styles.citationBox}>
            <code className={styles.citationText}>
              AxLabs: Deterministic Science Simulation Infrastructure.{' '}
              <a href="https://axlabs.io" target="_blank" rel="noopener noreferrer" className={styles.citationLink}>
                https://axlabs.io
              </a>
              {' '}OmenSystems, 2024.
            </code>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <a href="https://github.com/staticpayload/axlabs" className={styles.footerLink} target="_blank" rel="noopener noreferrer">GitHub Repository</a>
          <span className={styles.footerSeparator}>·</span>
          <Link to="/about" className={styles.footerLink}>About AxLabs</Link>
        </div>
      </footer>
    </div>
  );
}
